import ReactGA from "react-ga";
import isPes from "src/helpers/isPes";
import { http } from "src/newApi";

export interface DeckFlashcard {
	id: number;
	createdAt: string;
	updatedAt: string;
	front: string;
	back: string;
	userId: number;
	permission: string;
}

export const DeckType = {
	SpacedRepetition: "spaced_repetition",
	KnowDontKnow: "know_dont_know",
	View: "view",
} as const;
export type DeckType = (typeof DeckType)[keyof typeof DeckType];

export function updateDeckType(deckId: number, type: DeckType): Promise<void> {
	return http
		.patch(`/v2${prefix()}/flashcards/decks/${deckId}/type`, { type })
		.then(() => undefined);
}

export interface AvailableDeckMode {
	type: DeckType;
	reset: boolean;
	isActive: boolean;
}

export function getAvailableDeckModes(
	deckId: number,
): Promise<AvailableDeckMode[]> {
	return http
		.get(`/v2${prefix()}/flashcards/decks/${deckId}/modes`)
		.then(({ data }) => data);
}

export interface DeckPreview {
	id: number;
	createdAt: string;
	updatedAt: string;
	name: string;
	description: string;
	permission: string;
	code: string;
	status: string;
	userId: number;
	parentId: number | null;
	type: DeckType;
	specId: number | null;
	isCompanyDeck: boolean;
	flashcards: DeckFlashcard[];
}

const prefix = () => (isPes() ? "/pes" : "");

export function getDeckPreview(deckId: number): Promise<DeckPreview> {
	return http
		.get(`/v2${prefix()}/flashcards/decks/${deckId}`)
		.then(({ data }) => data);
}

export interface CopyDeckResponse {
	id: number;
	code: string;
}

export function copyDeck(deckId: number): Promise<CopyDeckResponse> {
	return http
		.post(`/v2${prefix()}/flashcards/decks/${deckId}/use`)
		.then(({ data }) => data);
}

export interface UseDecksBulkPayload {
	deckIds: number[];
	folderIds?: number[];
}

// Klonuje wybrane zestawy do biblioteki usera (opcjonalnie dopina do folderów).
// Zwraca kopie {id, code} w kolejności wejścia.
export function copyAndUseDecks(
	payload: UseDecksBulkPayload,
): Promise<CopyDeckResponse[]> {
	return http
		.post(`/v2${prefix()}/flashcards/user/decks/use/bulk`, payload)
		.then(({ data }) => data);
}

export function restartKnowDontKnowDeck(deckId: number): Promise<void> {
	return http
		.post(`/v2${prefix()}/flashcards/decks/${deckId}/restart`)
		.then(() => undefined);
}

export interface GradeFlashcardPayload {
	flashcardRelatedId: number;
	deckId: number;
	grade: number;
}

export function gradeFlashcard({
	flashcardRelatedId,
	deckId,
	grade,
}: GradeFlashcardPayload): Promise<void> {
	return http
		.post(
			`/v2${prefix()}/flashcards/user/flashcards/${flashcardRelatedId}/grade`,
			{ deckId, grade },
		)
		.then(() => undefined);
}

export function generateDeck(body: Record<string, unknown>): Promise<number> {
	ReactGA.event({
		category: "Flashcards",
		action: "User generate deck",
		label: "Generate deck",
	});
	return http
		.post(`/v2${prefix()}/flashcards/user/deck`, body)
		.then(({ data }) => {
			if (typeof data !== "object") {
				return data;
			}
			return data.id;
		});
}

export type DeckPermission = "private" | "public";

export interface CreateFlashcardInput {
	front: string;
	back: string;
	tagsIds?: number[];
}

export interface CreateDeckInput {
	name: string;
	description?: string;
	decksIds?: number[];
	flashcards?: CreateFlashcardInput[];
}

export interface BulkCreateDecksPayload {
	permission: DeckPermission;
	folderIds?: number[];
	decks: CreateDeckInput[];
}

export function createDecksBulk(
	body: BulkCreateDecksPayload,
): Promise<string[]> {
	return http
		.post(`/v2${prefix()}/flashcards/user/decks/bulk`, body)
		.then(({ data }) => data);
}

// Kategoria publicznego zestawu — backend jest źródłem prawdy (availableCategories w odpowiedzi).
export type DeckCategory = "admin" | "users" | "essential";
export type DeckCategoryFilter = "all" | DeckCategory;

export interface DeckCategoryOption {
	value: DeckCategoryFilter;
	label: string;
}

export interface SearchDecksParams {
	page?: number;
	take?: number;
	orderBy?: string;
	order?: string;
	category?: DeckCategoryFilter;
}

export function searchDecks(
	search: string | undefined,
	queryParams: SearchDecksParams,
) {
	return http
		.get(`/v2${prefix()}/flashcards/decks/search`, {
			params: { search, ...queryParams },
		})
		.then(({ data }) => data);
}

export interface UserDecksParams {
	search?: string;
	page?: number;
	take?: number;
	orderBy?: string;
	order?: string;
}

export function getUserDecks(params?: UserDecksParams) {
	return http
		.get(`/v2${prefix()}/flashcards/user/decks/search`, { params })
		.then(({ data }) => data);
}

export type DeckStatsType = DeckType;

export interface DeckStats {
	deckId: number;
	deckType: DeckStatsType;
	total: number;
	counts: Record<string, number>;
}

export interface DeckStatsParams {
	startDate?: string;
	endDate?: string;
	type: DeckStatsType;
}

export function getDeckStats(
	deckId: number,
	params?: DeckStatsParams,
): Promise<DeckStats> {
	return http
		.get(`/v2${prefix()}/flashcards/users/statistics/${deckId}`, {
			// _ busts przeglądarkowy cache 304 (webview oddawał stare total:0)
			params: { ...params, _: Date.now() },
		})
		.then(({ data }) => data);
}

export interface DeckFlashcardWithStats {
	id: number;
	front: string;
	back: string;
	stats: Record<string, number>;
}

export interface DeckFlashcardsStats {
	id: number;
	name: string;
	type: DeckStatsType;
	flashcards: DeckFlashcardWithStats[];
	startDate: string;
	endDate: string;
}

export type DeckFlashcardsSort =
	| "updatedAt"
	| "bestKnown"
	| "worstKnown"
	| "mostAnswered"
	| "leastAnswered";

export interface DeckFlashcardsStatsParams extends DeckStatsParams {
	sort?: DeckFlashcardsSort;
}

export function getDeckFlashcardsStats(
	deckId: number,
	params?: DeckFlashcardsStatsParams,
): Promise<DeckFlashcardsStats> {
	return http
		.get(`/v2${prefix()}/flashcards/users/statistics/${deckId}/flashcards`, {
			params: { ...params, _: Date.now() },
		})
		.then(({ data }) => data);
}
